import { useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { notifyError, notify } from "../../../utils/toast";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";

// ─────────────────────────────────────────────────────────────
// GraphQL
// ─────────────────────────────────────────────────────────────

const ADMIN_PAYOUT_BALANCE = gql`
  query AdminPayoutBalance {
    adminPayoutBalance {
      availableAmount
      pendingAmount
    }
  }
`;

const ADMIN_PENDING_PAYOUT_SELLERS = gql`
  query AdminPendingPayoutSellers {
    adminPendingPayoutSellers {
      sellerId
      sellerName
      storeName
      storeId
      tossPaymentsSellerId
      tossSellerStatus
      totalAmount
      orderCount
      carriedAmount
      currentMonthAmount
    }
  }
`;

const ADMIN_EXECUTE_SELLER_PAYOUTS = gql`
  mutation AdminExecuteSellerPayouts($sellerIds: [Int!]) {
    adminExecuteSellerPayouts(sellerIds: $sellerIds) {
      ok
      successCount
      failedCount
      skippedCount
      totalAmount
      message
    }
  }
`;

const ADMIN_COMPLETE_MANUAL_SELLER_PAYOUTS = gql`
  mutation AdminCompleteManualSellerPayouts(
    $sellerIds: [Int!]!
    $memo: String
  ) {
    adminCompleteManualSellerPayouts(sellerIds: $sellerIds, memo: $memo) {
      ok
      successCount
      failedCount
      totalAmount
      payoutIds
      message
    }
  }
`;

const ADMIN_LEGACY_UNSETTLED_STORES = gql`
  query PayoutPageLegacyUnsettledStores {
    adminLegacyUnsettledStores {
      sellerId
      storeId
      storeTitle
      sellerName
      settlePrice
      orderCount
      unrequestedOrderCount
      rejectedOrderCount
    }
  }
`;

// ─────────────────────────────────────────────────────────────
// 타입
// ─────────────────────────────────────────────────────────────

interface PendingPayoutSellerRow {
  sellerId: number;
  sellerName: string | null;
  storeName: string | null;
  storeId: number | null;
  tossPaymentsSellerId: string | null;
  tossSellerStatus: string | null;
  totalAmount: number;
  orderCount: number;
  carriedAmount: number;
  currentMonthAmount: number;
}

interface LegacyUnsettledRow {
  sellerId: number;
  storeId: number;
  storeTitle: string;
  sellerName: string | null;
  settlePrice: number;
  orderCount: number;
  unrequestedOrderCount: number;
  rejectedOrderCount: number;
}

interface ExecuteResult {
  ok: boolean;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  totalAmount: number;
  message: string | null;
}

interface ManualCompleteResult {
  ok: boolean;
  successCount: number;
  failedCount: number;
  totalAmount: number;
  payoutIds: number[];
  message: string | null;
}

// ─────────────────────────────────────────────────────────────
// 유틸
// ─────────────────────────────────────────────────────────────

function formatKRW(v: number) {
  return v.toLocaleString("ko-KR") + "원";
}

/** KST 기준 전월을 "YYYY-MM" 형식으로 반환 */
function getPrevMonthYearMonth(): string {
  const now = new Date();
  const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  const year = kst.getUTCFullYear();
  const month = kst.getUTCMonth(); // 0-indexed
  const prevYear = month === 0 ? year - 1 : year;
  const prevMonth = month === 0 ? 12 : month;
  return `${prevYear}-${String(prevMonth).padStart(2, "0")}`;
}

/** "YYYY-MM" → "YYYY년 M월" */
function formatYearMonth(ym: string) {
  const [y, m] = ym.split("-");
  return `${y}년 ${parseInt(m)}월`;
}

const SELLER_STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  APPROVED: { label: "완료 ✅", cls: "bg-green-100 text-green-700" },
  PARTIALLY_APPROVED: { label: "완료 ✅", cls: "bg-green-100 text-green-700" },
  APPROVAL_REQUIRED: {
    label: "미완료 ⚠️",
    cls: "bg-yellow-100 text-yellow-700",
  },
  KYC_REQUIRED: { label: "미완료 ⚠️", cls: "bg-yellow-100 text-yellow-700" },
};

function SellerStatusBadge({ status }: { status: string | null }) {
  if (!status)
    return <span className="text-red-500 text-xs font-medium">미등록 ❌</span>;
  const info = SELLER_STATUS_LABEL[status] ?? {
    label: status,
    cls: "bg-gray-100 text-gray-600",
  };
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${info.cls}`}
    >
      {info.label}
    </span>
  );
}

function StepBadge({ step }: { step: number }) {
  return (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold mr-2 shrink-0">
      {step}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// 컴포넌트
// ─────────────────────────────────────────────────────────────

export default function PayoutManagement() {
  const prevYearMonth = getPrevMonthYearMonth();

  const [lastPayoutResult, setLastPayoutResult] =
    useState<ExecuteResult | null>(null);
  const [lastManualResult, setLastManualResult] =
    useState<ManualCompleteResult | null>(null);
  const [manualMemo, setManualMemo] = useState("폐업/미등록 수동지급");
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const {
    data: balanceData,
    loading: balanceLoading,
    error: balanceError,
  } = useQuery(ADMIN_PAYOUT_BALANCE, { fetchPolicy: "network-only" });
  const { data: legacyData, loading: legacyLoading } = useQuery(
    ADMIN_LEGACY_UNSETTLED_STORES,
    { fetchPolicy: "network-only" },
  );
  const {
    data: sellersData,
    loading: sellersLoading,
    refetch: refetchSellers,
  } = useQuery(ADMIN_PENDING_PAYOUT_SELLERS, { fetchPolicy: "network-only" });

  const [executeSellerPayouts, { loading: executeLoading }] = useMutation(
    ADMIN_EXECUTE_SELLER_PAYOUTS,
  );
  const [completeManualSellerPayouts, { loading: manualLoading }] = useMutation(
    ADMIN_COMPLETE_MANUAL_SELLER_PAYOUTS,
  );

  const balance = balanceData?.adminPayoutBalance;
  const legacyUnsettled: LegacyUnsettledRow[] =
    legacyData?.adminLegacyUnsettledStores ?? [];
  const legacyTotalAmount = legacyUnsettled.reduce(
    (s, r) => s + r.settlePrice,
    0,
  );
  const legacySellerIdSet = new Set(legacyUnsettled.map((r) => r.sellerId));
  const isPayable = (s: PendingPayoutSellerRow) =>
    s.tossSellerStatus === "APPROVED" ||
    s.tossSellerStatus === "PARTIALLY_APPROVED";

  const sellers: PendingPayoutSellerRow[] = [
    ...(sellersData?.adminPendingPayoutSellers ?? []),
  ].sort((a, b) => {
    const aPayable = isPayable(a) ? 0 : 1;
    const bPayable = isPayable(b) ? 0 : 1;
    if (aPayable !== bPayable) return aPayable - bPayable;
    return b.totalAmount - a.totalAmount;
  });

  // 집계 (전체 기준)
  const payableSellers = sellers.filter(isPayable);
  const skippedCount = sellers.length - payableSellers.length;
  const totalPayable = payableSellers.reduce((s, r) => s + r.totalAmount, 0);
  const totalCarried = payableSellers.reduce((s, r) => s + r.carriedAmount, 0);
  const totalCurrent = payableSellers.reduce(
    (s, r) => s + r.currentMonthAmount,
    0,
  );

  // 선택 관련
  const selectedSellers = sellers.filter((s) => selectedIds.has(s.sellerId));
  const selectedTotal = selectedSellers.reduce((s, r) => s + r.totalAmount, 0);
  const allChecked =
    sellers.length > 0 && sellers.every((s) => selectedIds.has(s.sellerId));
  const someChecked = selectedIds.size > 0 && !allChecked;

  const toggleAll = () => {
    if (allChecked) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sellers.map((s) => s.sellerId)));
    }
  };

  const toggleRow = (sellerId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sellerId)) next.delete(sellerId);
      else next.add(sellerId);
      return next;
    });
  };

  // STEP 2: 지급 실행
  const handleExecute = async () => {
    const targets = selectedIds.size > 0 ? selectedSellers : payableSellers;
    const isSelective = selectedIds.size > 0;

    if (targets.length === 0) {
      notify("지급 가능한 셀러가 없습니다.");
      return;
    }

    const targetTotal = targets.reduce((s, r) => s + r.totalAmount, 0);
    const confirmMsg = isSelective
      ? `선택한 ${targets.length}명 / 총 ${formatKRW(targetTotal)}을 지급 실행하시겠습니까?`
      : `전체 ${targets.length}명 / 총 ${formatKRW(targetTotal)}을 지급 실행하시겠습니까?\n(셀러 미등록 ${skippedCount}명은 제외됩니다)`;

    if (!window.confirm(confirmMsg)) return;

    try {
      const variables = isSelective
        ? { sellerIds: targets.map((s) => s.sellerId) }
        : {};
      const res = await executeSellerPayouts({ variables });
      const data = res.data?.adminExecuteSellerPayouts as
        | ExecuteResult
        | undefined;
      if (data?.ok) {
        setLastPayoutResult(data);
        setLastManualResult(null);
        setSelectedIds(new Set());
        notify(data.message ?? "지급 실행 완료");
        await refetchSellers();
      } else {
        notifyError("지급 실행에 실패했습니다.");
      }
    } catch (e) {
      notifyError(e instanceof Error ? e.message : "지급 실행 오류");
    }
  };

  // STEP 2: 수동 정산 모달 열기 (선택 셀러 필수)
  const openManualModal = () => {
    if (selectedSellers.length === 0) {
      notify("수동 정산은 셀러를 선택한 뒤 실행할 수 있습니다.");
      return;
    }
    setManualMemo("폐업/미등록 수동지급");
    setManualModalOpen(true);
  };

  const closeManualModal = () => {
    if (manualLoading) return;
    setManualModalOpen(false);
  };

  // STEP 2: 수동 정산 완료 실행 (토스 미경유)
  const handleManualComplete = async () => {
    if (selectedSellers.length === 0) {
      notify("수동 정산은 셀러를 선택한 뒤 실행할 수 있습니다.");
      return;
    }

    try {
      const res = await completeManualSellerPayouts({
        variables: {
          sellerIds: selectedSellers.map((s) => s.sellerId),
          memo: manualMemo.trim() || "폐업/미등록 수동지급",
        },
      });
      const data = res.data?.adminCompleteManualSellerPayouts as
        | ManualCompleteResult
        | undefined;
      if (data?.ok) {
        setLastManualResult(data);
        setLastPayoutResult(null);
        setSelectedIds(new Set());
        setManualModalOpen(false);
        notify(data.message ?? "수동 정산 완료");
        await refetchSellers();
      } else {
        notifyError(data?.message ?? "수동 정산 완료에 실패했습니다.");
      }
    } catch (e) {
      notifyError(e instanceof Error ? e.message : "수동 정산 완료 오류");
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <h1 className="mb-1 text-2xl font-bold">정산 지급 관리</h1>
      <p className="mb-8 text-sm text-gray-500">
        4월 1일 이후 Order 기준으로 전월까지 미지급 정산을 토스페이먼츠
        지급대행으로 일괄 실행합니다.
      </p>

      {/* ── STEP 1: 지급 예정 확인 ── */}
      <div className="mb-4 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center">
          <StepBadge step={1} />
          <div>
            <h2 className="text-sm font-semibold text-gray-800">
              지급 예정 확인
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              토스 잔액과 전월까지({formatYearMonth(prevYearMonth)}) 미지급
              현황을 확인합니다
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
          {/* 토스 잔액 */}
          <div className="px-5 py-4">
            <p className="text-xs font-medium text-gray-500 mb-1">토스 잔액</p>
            {balanceLoading ? (
              <LoadingSpinnerInline />
            ) : balanceError ? (
              <p className="text-sm text-red-400">잔액 조회 실패</p>
            ) : (
              <>
                <p className="text-xl font-bold text-gray-900">
                  {formatKRW(balance?.availableAmount ?? 0)}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  처리중: {formatKRW(balance?.pendingAmount ?? 0)}
                </p>
              </>
            )}
          </div>
          {/* 지급 가능 */}
          <div className="px-5 py-4">
            <p className="text-xs font-medium text-blue-600 mb-1">
              지급 가능 (셀러 등록 완료)
            </p>
            {sellersLoading ? (
              <LoadingSpinnerInline />
            ) : (
              <>
                <p className="text-xl font-bold text-blue-700">
                  {payableSellers.length}명
                </p>
                <p className="text-xs text-blue-400 mt-0.5">
                  총 {formatKRW(totalPayable)}
                </p>
              </>
            )}
          </div>
          {/* 이월금 합계 */}
          <div className="px-5 py-4">
            <p className="text-xs font-medium text-orange-600 mb-1">
              이월금 합계
            </p>
            {sellersLoading ? (
              <LoadingSpinnerInline />
            ) : (
              <>
                <p className="text-xl font-bold text-orange-700">
                  {formatKRW(totalCarried)}
                </p>
                <p className="text-xs text-orange-400 mt-0.5">
                  {formatYearMonth(prevYearMonth)} 이전 미지급
                </p>
              </>
            )}
          </div>
          {/* 스킵 예정 */}
          <div className="px-5 py-4">
            <p className="text-xs font-medium text-yellow-700 mb-1">
              셀러 미등록 (스킵 예정)
            </p>
            {sellersLoading ? (
              <LoadingSpinnerInline />
            ) : (
              <>
                <p className="text-xl font-bold text-yellow-700">
                  {skippedCount}명
                </p>
                <p className="text-xs text-yellow-500 mt-0.5">
                  토스 셀러 등록 필요
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── STEP 2: 지급 실행 ── */}
      <div className="mb-8 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center">
          <StepBadge step={2} />
          <div>
            <h2 className="text-sm font-semibold text-gray-800">지급 실행</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              전월까지 미지급 정산 전체를 Payout 생성 + 토스페이먼츠 지급 일괄
              처리합니다
            </p>
          </div>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handleExecute}
              disabled={executeLoading || manualLoading || sellersLoading}
              className="rounded-lg bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {executeLoading
                ? "지급 실행 중..."
                : selectedIds.size > 0
                  ? `선택 ${selectedIds.size}명 지급 실행 (${formatKRW(selectedTotal)})`
                  : "지급 가능 셀러 전체 지급 실행"}
            </button>
            <button
              onClick={openManualModal}
              disabled={
                executeLoading ||
                manualLoading ||
                sellersLoading ||
                selectedIds.size === 0
              }
              className="rounded-lg border border-orange-300 bg-orange-50 px-6 py-2.5 text-sm font-semibold text-orange-700 shadow-sm hover:bg-orange-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {selectedIds.size > 0
                ? `선택 ${selectedIds.size}명 수동 정산 완료 (${formatKRW(selectedTotal)})`
                : "수동 정산 완료 (셀러 선택 필요)"}
            </button>
            {selectedIds.size > 0 && (
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-gray-400 hover:text-gray-600 underline"
              >
                선택 해제
              </button>
            )}
            {selectedIds.size === 0 && (
              <p className="text-xs text-gray-400">
                테이블에서 특정 셀러를 선택하면 선택 지급/수동 정산으로
                전환됩니다
              </p>
            )}
          </div>

          {lastPayoutResult && (
            <div className="rounded-lg border border-gray-100 overflow-hidden">
              <div className="flex items-center justify-between bg-gray-50 px-4 py-2 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-600">
                  지급 실행 결과
                </p>
                <button
                  onClick={() => setLastPayoutResult(null)}
                  className="text-xs text-gray-300 hover:text-gray-500"
                >
                  닫기
                </button>
              </div>
              <div className="grid grid-cols-4 divide-x divide-gray-100">
                {[
                  {
                    label: "성공",
                    value: `${lastPayoutResult.successCount}명`,
                    cls: "text-green-600",
                  },
                  {
                    label: "총 지급액",
                    value: formatKRW(lastPayoutResult.totalAmount),
                    cls: "text-indigo-600",
                  },
                  {
                    label: "실패",
                    value: `${lastPayoutResult.failedCount}명`,
                    cls: "text-red-500",
                  },
                  {
                    label: "스킵(미등록)",
                    value: `${lastPayoutResult.skippedCount}명`,
                    cls: "text-yellow-600",
                  },
                ].map((item) => (
                  <div key={item.label} className="px-4 py-3 text-center">
                    <p className="text-xs text-gray-400 mb-0.5">{item.label}</p>
                    <p className={`text-base font-bold ${item.cls}`}>
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {lastManualResult && (
            <div className="rounded-lg border border-orange-100 overflow-hidden">
              <div className="flex items-center justify-between bg-orange-50 px-4 py-2 border-b border-orange-100">
                <p className="text-xs font-semibold text-orange-700">
                  수동 정산 완료 결과
                </p>
                <button
                  onClick={() => setLastManualResult(null)}
                  className="text-xs text-orange-300 hover:text-orange-500"
                >
                  닫기
                </button>
              </div>
              <div className="grid grid-cols-3 divide-x divide-orange-100">
                {[
                  {
                    label: "성공",
                    value: `${lastManualResult.successCount}명`,
                    cls: "text-green-600",
                  },
                  {
                    label: "총 금액",
                    value: formatKRW(lastManualResult.totalAmount),
                    cls: "text-orange-700",
                  },
                  {
                    label: "실패",
                    value: `${lastManualResult.failedCount}명`,
                    cls: "text-red-500",
                  },
                ].map((item) => (
                  <div key={item.label} className="px-4 py-3 text-center">
                    <p className="text-xs text-gray-400 mb-0.5">{item.label}</p>
                    <p className={`text-base font-bold ${item.cls}`}>
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
              {lastManualResult.message && (
                <p className="px-4 py-2 text-xs text-gray-500 border-t border-orange-100">
                  {lastManualResult.message}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── 레거시 미정산 현황 (3월 31일 이전) ── */}
      <div className="mb-8 overflow-hidden rounded-xl border border-amber-200 shadow-sm">
        <button
          onClick={() => setLegacyOpen((v) => !v)}
          className="w-full flex items-center justify-between bg-amber-50 px-4 py-3 border-b border-amber-200 hover:bg-amber-100/60 transition-colors text-left"
        >
          <div>
            <h2 className="text-sm font-semibold text-amber-800">
              ⚠️ 레거시 미정산 현황{" "}
              <span className="text-amber-500 font-normal">
                (~3월 31일 이전)
              </span>
            </h2>
            <p className="text-xs text-amber-600 mt-0.5">
              기존 정산 방식(사장님 요청)으로 아직 정산받지 못한 매장
              목록입니다. 별도 처리가 필요합니다.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0 ml-4">
            {!legacyLoading && legacyUnsettled.length > 0 && (
              <div className="text-right text-xs text-amber-700">
                <p className="font-bold">{legacyUnsettled.length}개 매장</p>
                <p>{formatKRW(legacyTotalAmount)}</p>
              </div>
            )}
            <span className="text-amber-500 text-base">
              {legacyOpen ? "▲" : "▼"}
            </span>
          </div>
        </button>
        {legacyOpen &&
          (legacyLoading ? (
            <div className="flex justify-center py-6">
              <LoadingSpinnerInline />
            </div>
          ) : legacyUnsettled.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">
              레거시 미정산 매장이 없습니다 ✓
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-50/50 border-b border-amber-100">
                    <th className="px-4 py-2.5 text-left font-semibold text-amber-700">
                      매장명
                    </th>
                    <th className="px-4 py-2.5 text-center font-semibold text-amber-700">
                      주문수
                    </th>
                    <th className="px-4 py-2.5 text-center font-semibold text-amber-700">
                      미요청
                    </th>
                    <th className="px-4 py-2.5 text-center font-semibold text-amber-700">
                      거절
                    </th>
                    <th className="px-4 py-2.5 text-right font-semibold text-amber-700">
                      정산금액
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-50">
                  {legacyUnsettled.map((row) => (
                    <tr
                      key={row.storeId}
                      className="hover:bg-amber-50/30 transition-colors"
                    >
                      <td className="px-4 py-2.5 font-medium text-gray-800">
                        {row.storeTitle}
                        {row.sellerName && (
                          <span className="ml-1.5 text-xs text-gray-400">
                            {row.sellerName}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-center text-gray-600">
                        {row.orderCount}
                      </td>
                      <td className="px-4 py-2.5 text-center text-yellow-600">
                        {row.unrequestedOrderCount}
                      </td>
                      <td className="px-4 py-2.5 text-center text-red-500">
                        {row.rejectedOrderCount}
                      </td>
                      <td className="px-4 py-2.5 text-right font-semibold text-amber-800">
                        {formatKRW(row.settlePrice)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-amber-200 bg-amber-50">
                    <td
                      colSpan={4}
                      className="px-4 py-2.5 text-sm font-bold text-amber-800"
                    >
                      합계 {legacyUnsettled.length}개 매장
                    </td>
                    <td className="px-4 py-2.5 text-right font-bold text-amber-900">
                      {formatKRW(legacyTotalAmount)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ))}
      </div>

      {/* ── 지급 예정 목록 테이블 ── */}
      <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div>
            <h2 className="text-sm font-semibold text-gray-700">
              지급 예정 목록
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              체크박스로 특정 셀러만 선택해 지급할 수 있습니다. 미선택 시 지급
              가능 셀러 전체를 처리합니다.
            </p>
          </div>
          <button
            onClick={() => refetchSellers()}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            새로고침
          </button>
        </div>

        {sellersLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinnerInline />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="pl-4 pr-2 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => {
                        if (el) el.indeterminate = someChecked;
                      }}
                      onChange={toggleAll}
                      className="w-4 h-4 rounded cursor-pointer accent-indigo-600"
                    />
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">
                    매장명
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">
                    셀러 등록
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-orange-600">
                    이월금
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-blue-600">
                    해당월금액
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">
                    총액
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">
                    지급 가능 여부
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">
                    레거시
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sellers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="py-8 text-center text-sm text-gray-400"
                    >
                      지급 예정 정산이 없습니다. (4월 1일 이후 확정된 주문이
                      없거나 모두 지급 완료)
                    </td>
                  </tr>
                ) : (
                  sellers.map((row) => {
                    const canPay =
                      row.tossSellerStatus === "APPROVED" ||
                      row.tossSellerStatus === "PARTIALLY_APPROVED";
                    const hasLegacy = legacySellerIdSet.has(row.sellerId);
                    const isChecked = selectedIds.has(row.sellerId);
                    // 체크된 행은 opacity 해제
                    const dimCls = isChecked
                      ? ""
                      : !canPay
                        ? "opacity-60"
                        : hasLegacy
                          ? "opacity-50"
                          : "";
                    return (
                      <tr
                        key={row.sellerId}
                        onClick={() => toggleRow(row.sellerId)}
                        className={`cursor-pointer hover:bg-indigo-50/40 transition-colors ${dimCls} ${isChecked ? "bg-indigo-50" : ""}`}
                      >
                        <td className="pl-4 pr-2 py-3">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleRow(row.sellerId)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 rounded cursor-pointer accent-indigo-600"
                          />
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-800">
                          {row.storeName ??
                            row.sellerName ??
                            `셀러 #${row.sellerId}`}
                          <span className="ml-1.5 text-xs text-gray-400">
                            {row.orderCount}건
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <SellerStatusBadge status={row.tossSellerStatus} />
                        </td>
                        <td className="px-4 py-3 text-right text-orange-700 font-medium">
                          {row.carriedAmount > 0 ? (
                            formatKRW(row.carriedAmount)
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-blue-700 font-medium">
                          {row.currentMonthAmount > 0 ? (
                            formatKRW(row.currentMonthAmount)
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">
                          {formatKRW(row.totalAmount)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {canPay ? (
                            <span className="inline-block rounded-full px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700">
                              지급 가능
                            </span>
                          ) : (
                            <span className="inline-block rounded-full px-2 py-0.5 text-xs font-medium bg-red-50 text-red-500">
                              셀러 미등록 — 스킵
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {hasLegacy ? (
                            <span className="inline-block rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700">
                              미정산 있음
                            </span>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>

              {sellers.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-gray-300 bg-gray-100">
                    <td />
                    <td className="px-4 py-3 text-sm font-bold text-gray-700">
                      합계 {sellers.length}명
                      {skippedCount > 0 && (
                        <span className="ml-2 text-xs font-normal text-red-400">
                          (스킵 {skippedCount}명 제외)
                        </span>
                      )}
                    </td>
                    <td />
                    <td className="px-4 py-3 text-right text-sm font-bold text-orange-700">
                      {formatKRW(totalCarried)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-blue-700">
                      {formatKRW(totalCurrent)}
                    </td>
                    <td className="px-4 py-3 text-right text-base font-bold text-indigo-700">
                      {formatKRW(totalPayable)}
                    </td>
                    <td />
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {/* 수동 정산 완료 모달 */}
      {manualModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="manual-payout-modal-title"
            className="w-full max-w-md rounded-xl bg-white shadow-lg"
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <h2
                id="manual-payout-modal-title"
                className="text-base font-semibold text-gray-900"
              >
                수동 정산 완료
              </h2>
              <button
                type="button"
                onClick={closeManualModal}
                disabled={manualLoading}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-40"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 px-5 py-4">
              <div className="rounded-lg bg-orange-50 px-3 py-2 text-sm text-orange-800">
                <p>
                  선택 {selectedSellers.length}명 /{" "}
                  <span className="font-semibold">
                    {formatKRW(selectedTotal)}
                  </span>
                </p>
                <p className="mt-1 text-xs text-orange-700">
                  토스페이먼츠를 거치지 않습니다. 계좌이체 등 실지급을 완료한
                  뒤에만 실행하세요.
                </p>
              </div>

              <div>
                <label
                  htmlFor="manual-memo"
                  className="mb-1.5 block text-sm font-medium text-gray-700"
                >
                  수동 정산 메모
                </label>
                <input
                  id="manual-memo"
                  type="text"
                  value={manualMemo}
                  onChange={(e) => setManualMemo(e.target.value)}
                  disabled={manualLoading}
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-orange-300 focus:outline-none focus:ring-1 focus:ring-orange-300 disabled:bg-gray-50"
                  placeholder="폐업/미등록 수동지급"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
              <button
                type="button"
                onClick={closeManualModal}
                disabled={manualLoading}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleManualComplete}
                disabled={manualLoading}
                className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {manualLoading ? "처리 중..." : "수동 정산 완료"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
